/**
 * Deps Check Command
 * Kiểm tra các packages cần upgrade mà không thực hiện upgrade
 */
import { Command } from 'commander';
import chalk from 'chalk';
import Table from 'cli-table3';
import ora from 'ora';
import { DepsDetectorService } from '../../services/deps-detector.service.js';
import { DepsService } from '../../services/deps.service.js';
import { DepsPhpService } from '../../services/deps-php.service.js';
import { DepsPythonService } from '../../services/deps-python.service.js';
import type { DepsServiceInterface, CheckResult } from '../../types/deps.types.js';

export function createDepsCheckCommand(): Command {
    const checkCommand = new Command('check')
        .description('Kiểm tra các packages cần upgrade')
        .action(async () => {
            const cwd = process.cwd();
            const detector = new DepsDetectorService();

            // Detect project types
            const spinner = ora('Đang phát hiện loại project...').start();
            const projects = await detector.detectProjects(cwd);

            if (projects.length === 0) {
                spinner.fail('Không tìm thấy project nào được hỗ trợ');
                console.log();
                console.log(chalk.dim('Hỗ trợ: Node.js (package.json), PHP (composer.json), Python (requirements.txt/Pipfile)'));
                process.exit(1);
            }

            spinner.succeed(`Phát hiện ${projects.length} project:`);
            for (const project of projects) {
                const icon = project.ecosystem === 'node' ? '📦' : project.ecosystem === 'php' ? '🐘' : '🐍';
                const label = project.ecosystem === 'node' ? 'Node.js' :
                             project.ecosystem === 'php' ? 'PHP/Composer' : 'Python';
                console.log(`   ${icon} ${label} (${project.manager})`);
            }
            console.log();

            // Check each ecosystem
            for (const project of projects) {
                await checkEcosystem(project.ecosystem, project.manager, cwd);
            }

            console.log();
            console.log(chalk.dim('💡 Chạy "jai1 deps upgrade" để nâng cấp packages.'));
        });

    return checkCommand;
}

async function checkEcosystem(
    ecosystem: 'node' | 'php' | 'python',
    manager: string,
    cwd: string
): Promise<void> {
    // Get appropriate service
    let service: DepsServiceInterface;
    let icon: string;
    let label: string;

    switch (ecosystem) {
        case 'node':
            service = new DepsService();
            icon = '📦';
            label = `Node.js (${manager})`;
            break;
        case 'php':
            service = new DepsPhpService();
            icon = '🐘';
            label = 'PHP/Composer';
            break;
        case 'python':
            service = new DepsPythonService();
            icon = '🐍';
            label = `Python (${manager})`;
            break;
    }

    // Check for outdated packages
    const spinner = ora(`Đang kiểm tra ${label}...`).start();

    let result: CheckResult;
    try {
        result = await service.check(cwd, (completed, total) => {
            spinner.text = `Đang kiểm tra ${label}... (${completed}/${total})`;
        });
    } catch (error) {
        spinner.fail(`Lỗi kiểm tra ${label}`);
        console.log(chalk.red((error as Error).message));
        console.log();
        return;
    }

    if (result.packages.length === 0) {
        spinner.succeed(`${label} - Tất cả packages đã cập nhật`);
        console.log();
        return;
    }

    spinner.succeed(`${label} - ${result.packages.length} packages có thể nâng cấp`);
    console.log();

    // Display table
    const table = new Table({
        head: [
            chalk.cyan('Package'),
            chalk.cyan('Hiện tại'),
            chalk.cyan('Mới nhất'),
            chalk.cyan('Loại')
        ],
        style: {
            head: [],
            border: ['dim']
        }
    });

    for (const pkg of result.packages) {
        const upgradeIcon = pkg.upgradeType === 'major' ? '🔴' :
                           pkg.upgradeType === 'minor' ? '🟡' : '🟢';

        table.push([
            `${upgradeIcon} ${pkg.name}`,
            chalk.dim(pkg.current),
            chalk.green(pkg.latest),
            pkg.type === 'dev' ? chalk.dim('dev') : 'dep'
        ]);
    }

    console.log(table.toString());
    console.log();

    // Show Laravel warnings if applicable
    if (result.isLaravel) {
        // Check if there are blocked Laravel packages
        const blockedPackages = result.packages.filter(p => p.blockedReason);
        if (blockedPackages.length > 0) {
            console.log(chalk.yellow('⚠️  Laravel major version upgrades blocked (nguy hiểm):'));
            for (const pkg of blockedPackages) {
                console.log(chalk.dim(`   - ${pkg.name}: ${pkg.current} → ${pkg.latest}`));
            }
            console.log();
            console.log(chalk.dim('💡 Để nâng cấp Laravel major version, chạy thủ công:'));
            console.log(chalk.dim(`   composer require ${blockedPackages[0].name}:^${blockedPackages[0].latest}`));
            console.log();
        }
    }
}
